import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import { initSentry } from "./sentry";
import LogRocket from 'logrocket';
import "./index.css";
import "./tw_output.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { BrowserRouter } from "react-router-dom";
import { initConfig, getConfig } from "./services/config.service";
import { installModalScrollLock } from "./utils/modalScrollLock";

// Background-page scrolling is blocked while any modal overlay is open (App.css
// handles the viewport scrollbar; this handles inner scrollable panels).
installModalScrollLock();

// Initialize configuration then render app
const renderApp = async () => {
  // Fetch runtime configuration from API
  await initConfig();
  const config = getConfig();

  // Initialize Sentry from runtime config (DSN + environment come from the API,
  // like logRocketAppId). No-ops if no DSN is configured for this environment.
  initSentry(config);

  // Initialize LogRocket for session recording (only if configured)
  if (config.logRocketAppId) {
    LogRocket.init(config.logRocketAppId);
  }

  const root = ReactDOM.createRoot(document.getElementById("root"));
  root.render(
    <React.StrictMode>
      <Sentry.ErrorBoundary fallback={<AppCrashFallback />}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </Sentry.ErrorBoundary>
    </React.StrictMode>,
  );
};

/**
 * Minimal fallback shown if a render error escapes to the root. Previously an
 * unhandled render error left the agent staring at a blank white page; now they
 * get a recover action and the error is reported to Sentry automatically.
 */
function AppCrashFallback() {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        padding: 24,
        textAlign: "center",
        fontFamily: "system-ui, sans-serif",
      }}
    >
      <h1 style={{ fontSize: 20, marginBottom: 8 }}>Something went wrong</h1>
      <p style={{ color: "#555", marginBottom: 20 }}>
        The page hit an unexpected error. Reloading usually fixes it.
      </p>
      <button
        onClick={() => window.location.reload()}
        style={{
          padding: "10px 20px",
          borderRadius: 8,
          border: "none",
          background: "#1a73e8",
          color: "#fff",
          fontSize: 15,
          cursor: "pointer",
        }}
      >
        Reload
      </button>
    </div>
  );
}

renderApp();

// If you want to start measuring performance in your app, pass a function
